export type NativeNotificationEventBaseFields = {
    /**
     * A unique identifier for this notification delivery.
     */
    deliveryId: string;
    /**
     * The ID of the user who should receive this notification.
     */
    userId: string;
    /**
     * The date and time at which this notification delivery was first created.
     */
    createdDateTime: string;
    content: {
        /**
         * The title of the notification to display on the device.
         */
        title: string;
        /**
         * The body of the notification to display on the device.
         */
        body: string;
        /**
         * Additional data to include with the notification.
         *
         * **Note:** This data must be less than 4KB in size.
         */
        data: Record<string, any>;
    };
};

export type NativeNotificationRequeueFields = {
    /**
     * The number of times the native notification event handler has sent this event's chain back to the native
     * notification queue. An event sent while processing a batch continues from the highest count in that batch.
     *
     * Absent on events sent to the queue by other services.
     */
    requeueCount?: number;
};

/**
 * An event to deliver a notification to specific user's device(s).
 */
export type DeliverUserNativeNotificationEvent = {
    eventType: 'deliverUserNativeNotification';
} & NativeNotificationEventBaseFields &
    NativeNotificationRequeueFields;

/**
 * An event to deliver a notification to a specific device for a specific user.
 */
export type DeliverDeviceNativeNotificationEvent = {
    eventType: 'deliverDeviceNativeNotification';
    /**
     * The push token for the device to which this notification should be delivered.
     */
    devicePushToken: string;
    /**
     * The number of times this specific notification has been attempted to be delivered to this device.
     */
    attemptCount: number;
    /**
     * The ID of the parent delivery for this notification.
     *
     * This will only be present if this device notification delivery was spawned from a user notification delivery.
     */
    parentDeliveryId?: string;
} & NativeNotificationEventBaseFields &
    NativeNotificationRequeueFields;

/**
 * An event to check the receipt of a notification delivery on a specific device.
 */
export type CheckDeviceNativeNotificationReceiptEvent = {
    eventType: 'checkDeviceNativeNotificationReceipt';
    /**
     * The ID of the receipt to check (this is returned from Expo Push Service).
     */
    receiptId: string;
    /**
     * The date and time at which the delivery corresponding to this receipt was sent.
     */
    sentDateTime: string;
    /**
     * The original event related to this receipt.
     *
     * This event will be resent upon failure if the `attemptCount` is less than the maximum number of attempts.
     */
    originalEvent: DeliverDeviceNativeNotificationEvent;
} & NativeNotificationRequeueFields;

export type NativeNotificationEvent =
    | DeliverUserNativeNotificationEvent
    | DeliverDeviceNativeNotificationEvent
    | CheckDeviceNativeNotificationReceiptEvent;
